import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Calendar, Clock, User, MapPin, CheckCircle, AlertTriangle, DollarSign, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { companyInfo } from '../../data/mock';

const ACRepairRoseville = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFCF7]" data-testid="blog-post-ac-repair">
      {/* Navigation Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/blog')}
            data-testid="back-to-blog-btn"
            className="flex items-center gap-2 text-[#622B62] hover:text-[#DA9C3A] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Back to Blog
          </button>
          <a href={`tel:${companyInfo.phone}`}>
            <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full">
              <Phone size={16} className="mr-2" />
              {companyInfo.phone}
            </Button>
          </a>
        </div>
      </div>

      {/* Article Hero */}
      <div className="bg-[#622B62] text-white py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
        <div className="max-w-3xl mx-auto px-4 relative z-10">
          <span className="inline-block bg-[#DA9C3A] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">AC Repair</span>
          <h1 className="romano-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" data-testid="article-heading" data-speakable="true">
            AC Repair in Roseville, CA — When to Call, What to Expect, and How to Avoid Getting Overcharged
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1"><User size={14} /> Juliano Romano</span>
            <span className="flex items-center gap-1"><Calendar size={14} /> June 9, 2026</span>
            <span className="flex items-center gap-1"><Clock size={14} /> 12 min read</span>
            <span className="flex items-center gap-1"><MapPin size={14} /> Roseville, CA</span>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">

          {/* Intro */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-10 shadow-sm" data-speakable="true">
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              Your AC just stopped working. It's 104 degrees outside, the kids are cranky, the dog is panting on the tile floor, and you're Googling "<strong>AC repair Roseville CA</strong>" from your phone while standing in front of an open freezer. Sound familiar?
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mb-0">
              I'm Juliano Romano. I've been running AC repair calls in Roseville and the surrounding area for over 20 years, and I've walked into that exact scenario more times than I can count. This guide is everything I'd want a friend to know before they call <em>any</em> HVAC company — including mine. Let's talk about what's probably going on with your AC, what a fair repair looks like, and how to make sure you don't get taken advantage of when you're desperate.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-[#622B62]/5 rounded-2xl p-6 mb-10">
            <h2 className="romano-heading text-xl font-bold text-[#622B62] mb-4">What's in This Guide</h2>
            <ul className="space-y-2">
              {[
                '"My AC Isn\'t Cooling" — The Most Common Roseville AC Problems',
                'What to Try Before You Call for AC Repair',
                'How Much Does AC Repair Cost in Roseville, CA?',
                'What Happens During a Professional AC Repair Visit',
                'Repair vs. Replace — The Honest Conversation',
                'Red Flags: How to Spot a Bad HVAC Company',
                'Emergency AC Repair in Roseville — What to Know',
                'Every City We Cover for AC Repair',
                'Straight Talk from Juliano',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-[#DA9C3A] font-bold">{i + 1}.</span> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Section 1 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            "My AC Isn't Cooling" — The Most Common Roseville AC Problems
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            After two decades of AC repair in Roseville, CA, I can tell you that about 80% of the calls we get fall into a handful of categories. Here's what we see most often — and what's probably going on with your system right now:
          </p>
          <div className="space-y-4 my-6">
            {[
              { problem: 'Refrigerant leak', detail: 'This is the big one. If your AC is blowing air but it\'s not cold, low refrigerant is usually the culprit. The system isn\'t "out of freon" — it has a leak somewhere, and we need to find it and fix it. In Roseville\'s heat, even a small leak can make your system completely useless within a few weeks.', freq: 'Very common' },
              { problem: 'Failed capacitor', detail: 'Capacitors are like the battery that kickstarts your compressor and fan motors. They wear out — especially in our heat. When a capacitor fails, your AC might hum but not turn on, or the fan might spin sluggishly. Good news: it\'s usually a quick, affordable fix.', freq: 'Very common' },
              { problem: 'Frozen evaporator coil', detail: 'Sounds weird that something can freeze in 100-degree weather, right? It happens when airflow is restricted (dirty filter, blocked vents) or refrigerant is low. You\'ll notice ice on the indoor unit or water pooling around your furnace.', freq: 'Common' },
              { problem: 'Dirty condenser coil', detail: 'Your outdoor unit collects dirt, cottonwood seeds, dust, and debris — especially here in the Sacramento Valley. When the condenser coil gets caked up, it can\'t release heat efficiently, and your AC struggles to cool your home.', freq: 'Common' },
              { problem: 'Electrical issues', detail: 'Corroded wires, loose connections, tripped breakers, blown fuses. Roseville\'s extreme heat cycling — 60°F at night to 105°F in the afternoon — causes metal to expand and contract, which loosens electrical connections over time.', freq: 'Moderate' },
              { problem: 'Thermostat malfunction', detail: 'Sometimes the AC is fine — it\'s the thermostat that\'s gone haywire. We\'ve seen thermostats in direct sunlight reading 85°F when the room is actually 72°F, causing the system to run non-stop.', freq: 'Moderate' },
              { problem: 'Compressor failure', detail: 'This is the one nobody wants to hear. The compressor is the heart of your AC system, and when it fails, you\'re looking at a significant repair — or potentially a full system replacement if the unit is older.', freq: 'Less common' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-2 flex-1">
                    <Wrench size={18} className="text-[#622B62] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-bold text-[#622B62]">{item.problem}</span>
                      <p className="text-gray-600 mt-1 mb-0 text-sm">{item.detail}</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#DA9C3A] bg-[#DA9C3A]/10 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">{item.freq}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Section 2 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            What to Try Before You Call for AC Repair
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            I know this sounds funny coming from a guy who makes his living fixing ACs, but sometimes you don't need us. Before you call for AC repair in Roseville, try these things first — you might save yourself a service call:
          </p>
          <div className="grid gap-3 my-6">
            {[
              { step: 'Check your thermostat settings', why: 'Make sure it\'s set to COOL (not HEAT or FAN ONLY) and the temperature is set below the current room temp. You\'d be surprised how often this is the "problem."' },
              { step: 'Check the circuit breaker', why: 'Go to your electrical panel and look for a tripped breaker. Flip it off, then back on. If it trips again immediately, stop — you need a pro.' },
              { step: 'Check your air filter', why: 'Pull it out and look at it. If you can\'t see light through it, it\'s too dirty. A clogged filter can shut down your whole system. Swap it out and give your AC 30 minutes.' },
              { step: 'Check the outdoor unit', why: 'Go outside and look at your condenser. Is the fan spinning? Is it surrounded by weeds or debris? Clear at least 2 feet around it.' },
              { step: 'Check your vents', why: 'Make sure all supply and return vents are open and unblocked. Yes, even in rooms you don\'t use.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-[#DA9C3A] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">{i + 1}</span>
                </div>
                <div>
                  <span className="font-bold text-[#622B62]">{item.step}</span>
                  <p className="text-gray-600 mt-1 mb-0 text-sm">{item.why}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            Tried all five and still sweating? Okay, <em>now</em> call us. No judgment — at least you ruled out the easy stuff.
          </p>

          {/* Section 3 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            How Much Does AC Repair Cost in Roseville, CA?
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            This is the question everyone asks, and I'm going to give you a straight answer — something a lot of companies won't do. Here's what typical AC repairs cost in the Roseville area as of 2026:
          </p>
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden my-6">
            <div className="bg-[#622B62] text-white p-4">
              <h3 className="font-bold text-lg mb-0">Typical AC Repair Costs — Roseville, CA (2026)</h3>
            </div>
            <div className="divide-y">
              {[
                { repair: 'Capacitor replacement', range: '$150 – $350', note: 'Quick fix, common part' },
                { repair: 'Refrigerant recharge (no leak)', range: '$200 – $500', note: 'Depends on refrigerant type' },
                { repair: 'Refrigerant leak repair', range: '$400 – $1,200', note: 'Depends on location of leak' },
                { repair: 'Contactor replacement', range: '$150 – $400', note: 'Controls power to compressor' },
                { repair: 'Fan motor replacement', range: '$300 – $700', note: 'Indoor or outdoor motor' },
                { repair: 'Evaporator coil replacement', range: '$800 – $2,000', note: 'Labor-intensive repair' },
                { repair: 'Compressor replacement', range: '$1,500 – $3,000+', note: 'May be better to replace unit' },
                { repair: 'Circuit board replacement', range: '$400 – $900', note: 'The "brain" of your system' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex-1">
                    <span className="font-medium text-gray-800">{item.repair}</span>
                    <p className="text-gray-500 text-xs mb-0">{item.note}</p>
                  </div>
                  <span className="font-bold text-[#622B62] text-sm">{item.range}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#DA9C3A]/10 border-l-4 border-[#DA9C3A] p-5 rounded-r-xl my-6">
            <p className="text-gray-800 font-medium mb-0">
              A note on honesty: If someone quotes you $200 for a compressor replacement or $2,000 for a capacitor, something's off. These ranges are based on real pricing in the Roseville, Rocklin, and Folsom market. At Romano, we always provide a written estimate before we start any work — no surprises.
            </p>
          </div>

          {/* Section 4 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            What Happens During a Professional AC Repair Visit
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            I want to pull back the curtain so you know exactly what should happen when an HVAC tech shows up at your door in Roseville. Here's how we do it at Romano — and how any reputable company should handle it:
          </p>
          <div className="space-y-4 my-6">
            {[
              { step: 'Arrival & walkthrough', time: '5–10 min', detail: 'We show up on time (novel concept, I know). Our tech introduces themselves, listens to what you\'re experiencing, and asks a few questions. We don\'t just walk past you to the thermostat.' },
              { step: 'Diagnosis', time: '20–45 min', detail: 'This is where the real work happens. We check electrical readings, pressures, temperatures, airflow — the whole nine yards. We\'re not guessing. We\'re testing.' },
              { step: 'Honest explanation', time: '5–10 min', detail: 'We tell you what we found, in plain English. No scare tactics, no pressure. If there are multiple options, we lay them all out with costs so you can make an informed decision.' },
              { step: 'Written estimate', time: '5 min', detail: 'Before we touch a single part, you get a written estimate. You approve it, or you don\'t. No work starts without your green light.' },
              { step: 'The repair', time: 'Varies', detail: 'Most common AC repairs take 30 minutes to 2 hours. We carry the most common parts on our trucks, so we can usually finish in one visit.' },
              { step: 'Testing & verification', time: '10–15 min', detail: 'We don\'t just flip it on and leave. We run the system, check temperatures at the vents, verify pressures, and make sure everything is operating correctly before we pack up.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-[#622B62] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">{i + 1}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-[#622B62]">{item.step}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{item.time}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-0">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Section 5 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Repair vs. Replace — The Honest Conversation
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            This is where a lot of HVAC companies in Roseville lose people's trust. Some push new systems on everyone. Others keep patching old equipment that should've been retired years ago. Here's my honest take on when to repair and when to replace:
          </p>
          <div className="grid md:grid-cols-2 gap-6 my-6">
            <div className="bg-white rounded-2xl p-6 border-2 border-green-200 shadow-sm">
              <h3 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                <CheckCircle size={20} /> Repair Makes Sense When
              </h3>
              <ul className="space-y-2">
                {[
                  'Your system is less than 10 years old',
                  'The repair costs less than 50% of a new system',
                  'It\'s a common, straightforward fix (capacitor, contactor, fan motor)',
                  'The system has been well-maintained',
                  'You\'re not planning to sell the house soon',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 border-2 border-red-200 shadow-sm">
              <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                <AlertTriangle size={20} /> Replacement Makes Sense When
              </h3>
              <ul className="space-y-2">
                {[
                  'Your system is 15+ years old',
                  'You\'re facing a $2,000+ repair (compressor, coil)',
                  'You\'ve had 3+ repairs in the past 2 years',
                  'Your energy bills keep climbing despite maintenance',
                  'The system uses R-22 refrigerant (phased out, expensive)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            The way I see it: if you brought your car to a mechanic and the repair cost more than the car is worth, you wouldn't do it. Same principle here. I'll always give you my honest recommendation — even if that recommendation means less money in my pocket.
          </p>

          {/* Section 6 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Red Flags: How to Spot a Bad HVAC Company in Roseville
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Unfortunately, our industry has its share of bad actors — especially during Roseville's summer rush when desperate homeowners are easy targets. Watch out for these red flags:
          </p>
          <div className="space-y-3 my-6">
            {[
              { flag: 'They diagnose over the phone', detail: 'No legitimate tech can tell you what\'s wrong with your AC without seeing it. If someone quotes a repair price over the phone, hang up.' },
              { flag: 'They won\'t give you a written estimate', detail: 'If they start working before telling you the cost, that\'s a massive red flag. Always get it in writing.' },
              { flag: 'They pressure you to decide right now', detail: '"This price is only good today" is a classic high-pressure tactic. A good company gives you time to think.' },
              { flag: 'They recommend a full replacement for a new-ish system', detail: 'If your system is under 10 years old and someone says you need a whole new one, get a second opinion. Call us — we\'ll be honest.' },
              { flag: 'They can\'t show you a license', detail: 'Every HVAC contractor in California must have a C-20 license. Ask for the number and look it up on cslb.ca.gov.' },
              { flag: 'They charge for the estimate, then apply it to the repair', detail: 'This is a psychological trick to make you feel committed. At Romano, our diagnostic fee is straightforward — no games.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-red-50 rounded-xl p-4 border border-red-100">
                <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-bold text-red-800">{item.flag}</span>
                  <p className="text-gray-700 mt-1 mb-0 text-sm">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Section 7 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Emergency AC Repair in Roseville — What to Know
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            When it's 108°F and your AC dies at 9 PM, you need help now — not tomorrow morning. Here's what emergency AC repair looks like in Roseville:
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            At Romano Heating and Cooling, we offer <strong>24/7 emergency AC repair</strong>. That means nights, weekends, and holidays. Because heat emergencies don't check the calendar. A few things to know about emergency calls:
          </p>
          <ul className="space-y-2 my-6">
            {[
              'We prioritize safety situations — elderly residents, homes with infants, and medical conditions get top priority',
              'Our trucks carry the most common parts, so we can often complete the repair in one visit — even at 11 PM',
              'We provide the same written estimate process for emergency calls. No price gouging because it\'s after hours',
              'If we can\'t fix it that night, we\'ll get your system to a safe state and schedule the full repair for first thing in the morning',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-gray-700">
                <CheckCircle size={18} className="text-[#DA9C3A] mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="bg-[#DA9C3A]/10 border-l-4 border-[#DA9C3A] p-5 rounded-r-xl my-6">
            <p className="text-gray-800 font-medium mb-0">
              Pro tip: If your AC dies and you need to cool down while waiting for a tech, close all blinds, run bathroom exhaust fans to pull hot air out, wet a towel and hang it in front of a box fan, and head to a lower floor of your home if you have one. Stay hydrated.
            </p>
          </div>

          {/* Section 8 - GEO */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Every City We Cover for AC Repair
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Our headquarters is on Church Street in Roseville, but our trucks are on the road all day. If you need AC repair and you're in any of these communities, we can be there:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 my-6">
            {[
              { city: 'Roseville, CA', note: 'Home base — fastest response times' },
              { city: 'Rocklin, CA', note: 'Minutes away — we\'re there fast' },
              { city: 'Granite Bay, CA', note: 'Large homes, we handle them all' },
              { city: 'Lincoln, CA', note: 'Sun City and beyond' },
              { city: 'Loomis, CA', note: 'Small town, same great service' },
              { city: 'Folsom, CA', note: 'Quick trip down the highway' },
              { city: 'El Dorado Hills, CA', note: 'Foothills heat? We know it well' },
              { city: 'Citrus Heights, CA', note: 'Long-time service area' },
              { city: 'Orangevale, CA', note: 'Right in our neighborhood' },
              { city: 'Fair Oaks, CA', note: 'A community we know well' },
              { city: 'Carmichael, CA', note: 'Consistent, reliable repairs' },
              { city: 'Sacramento, CA', note: 'Covering greater Sacramento' },
              { city: 'Antelope, CA', note: 'North Sacramento area' },
              { city: 'Auburn, CA', note: 'Up into the foothills' },
              { city: 'Penryn, CA', note: 'Rural Placer County covered' },
              { city: 'Newcastle, CA', note: 'We drive the extra mile' },
              { city: 'Meadow Vista, CA', note: 'Yes, we come up the hill' },
              { city: 'North Highlands, CA', note: 'Part of our regular route' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <MapPin size={16} className="text-[#DA9C3A] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-gray-800 font-medium text-sm">{item.city}</span>
                  <p className="text-gray-500 text-xs mb-0">{item.note}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Section 9 - Personal */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Straight Talk from Juliano
          </h2>
          <div className="bg-[#622B62]/5 rounded-2xl p-8 my-6 border border-[#622B62]/10">
            <p className="text-gray-700 leading-relaxed mb-4">
              Here's the thing about AC repair in Roseville — there are a lot of companies who'll take your money. Some of them are great. But a lot of them see a broken AC in July as a chance to pad a bill, because they know you're uncomfortable and you'll say yes to almost anything.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              I've built this company on the idea that you can be successful by being honest. I don't need to upsell you a new system when a $200 capacitor will get you through another five summers. I don't need to charge you for parts you don't need. And I definitely don't need to scare you into a decision.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you call Romano for AC repair, you're going to get a real person on the phone — not a call center in another state. You're going to get a technician who lives in this community, whose kids go to school with your kids, who you might run into at the Roseville Farmers Market on Saturday morning.
            </p>
            <p className="text-gray-700 leading-relaxed mb-0">
              That accountability matters to me. And I think it matters to you, too.
            </p>
            <div className="mt-6 pt-6 border-t border-[#622B62]/10">
              <p className="font-bold text-[#622B62] mb-0">— Juliano Romano</p>
              <p className="text-gray-500 text-sm mb-0">Owner, Romano Heating and Cooling · LIC #1077499</p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#622B62] to-[#4A1F4A] rounded-3xl p-10 text-center text-white relative overflow-hidden my-12">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <h3 className="romano-heading text-2xl sm:text-3xl font-bold mb-4">
                Need AC Repair in Roseville? We're Here.
              </h3>
              <p className="text-white/90 mb-8 max-w-xl mx-auto">
                Whether it's an emergency or you just want a second opinion, give us a call. Free estimates, upfront pricing, and no surprises. Available 24/7.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:9162749021">
                  <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-10 py-6 text-lg rounded-full shadow-xl">
                    <Phone size={22} className="mr-2" />
                    Call (916) 274-9021
                  </Button>
                </a>
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#622B62] font-bold px-10 py-6 text-lg rounded-full"
                  onClick={() => navigate('/#contact')}
                >
                  Get a Free Estimate
                </Button>
              </div>
            </div>
          </div>

          {/* Related Links */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 my-8">
            <h4 className="font-bold text-[#622B62] mb-3">More from Romano Heating & Cooling</h4>
            <ul className="space-y-2">
              <li><Link to="/maintenance-plans" className="text-[#DA9C3A] font-medium hover:underline">Maintenance Plans — Prevent breakdowns starting at $9.99/mo</Link></li>
              <li><Link to="/blog/air-conditioner-service-roseville" className="text-[#DA9C3A] font-medium hover:underline">Air Conditioner Service in Roseville — Complete Homeowner Guide</Link></li>
              <li><Link to="/blog/air-conditioning-service-roseville-ca" className="text-[#DA9C3A] font-medium hover:underline">Air Conditioning Service Roseville CA — Technical Overview</Link></li>
              <li><Link to="/services/all" className="text-[#DA9C3A] font-medium hover:underline">All Heating & Cooling Services</Link></li>
              <li><Link to="/faq" className="text-[#DA9C3A] font-medium hover:underline">Frequently Asked Questions</Link></li>
            </ul>
          </div>

          {/* Author Box */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-start gap-5 mt-8">
            <img
              src={companyInfo.logo}
              alt="Romano Heating & Cooling"
              className="w-16 h-16 rounded-lg object-contain flex-shrink-0"
            />
            <div>
              <h4 className="font-bold text-[#622B62]">Juliano Romano</h4>
              <p className="text-sm text-gray-500 mb-2">Owner, Romano Heating & Cooling</p>
              <p className="text-gray-600 text-sm">
                Juliano has been providing honest, dependable AC repair in Roseville, CA since 2005. He and his team serve Roseville, Rocklin, Folsom, and the entire greater Sacramento region. When he's not on a service call, he's spending time with his family right here in the community. California contractor license #1077499.
              </p>
            </div>
          </div>

        </div>
      </article>

      {/* Footer */}
      <div className="bg-[#622B62] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={companyInfo.logo} alt="Romano Heating & Cooling" className="h-24 w-24 object-contain rounded-lg" />
            <div className="text-left">
              <h3 className="font-bold romano-heading">Romano Heating & Cooling</h3>
              <p className="text-white/70 text-sm">1316 Church Street, Roseville, CA 95678</p>
            </div>
          </div>
          <p className="text-[#DA9C3A] font-semibold mb-2">(916) 274-9021</p>
          <p className="text-white/60 text-sm">&copy; {new Date().getFullYear()} Romano Heating and Cooling. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ACRepairRoseville;
